import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Copy, Check, Loader2, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STEP_LABEL = { 1: "First outreach", 2: "Follow-up", 3: "Final nudge" };

export const PrepareEmailDialog = ({ open, onOpenChange, lead, onSent, batchInfo }) => {
  const [to, setTo] = useState("");
  const [step, setStep] = useState(1);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [brief, setBrief] = useState("");
  const [briefLoading, setBriefLoading] = useState(false);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    if (open && lead) {
      setCopied(false); setBrief(""); setLoading(true);
      api.prepareEmail(lead.id)
        .then((d) => {
          setTo(d.to); setStep(d.step);
          if (lead.ai_draft_body) { setSubject(lead.ai_draft_subject || d.subject); setBody(lead.ai_draft_body); setFromCache(true); }
          else { setSubject(d.subject); setBody(d.body); setFromCache(false); }
        })
        .catch(() => toast.error("Could not prepare email"))
        .finally(() => setLoading(false));
    }
  }, [open, lead]);

  const getBrief = async () => {
    setBriefLoading(true);
    try {
      const r = await api.aiBrief(lead.id, !!brief);
      setBrief(r.brief);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate brief"); }
    finally { setBriefLoading(false); }
  };

  const mailto = useMemo(
    () => `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
    [to, subject, body]
  );

  const aiDraft = async () => {
    setAiLoading(true);
    try {
      const d = await api.aiDraft(lead.id, step);
      setSubject(d.subject); setBody(d.body);
      toast.success("AI draft ready — tweak it, then send");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    finally { setAiLoading(false); }
  };

  const copyBody = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch (e) { toast.error("Copy failed"); }
  };

  const markSent = async () => {
    try {
      await api.logTouch(lead.id);
      toast.success(batchInfo ? "Sent — next lead" : "Logged — next step scheduled");
      onSent?.();
    } catch (e) { toast.error("Could not log touch"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="prepare-dialog">
        <DialogHeader>
          <DialogTitle>Prepare email — {lead?.business_name}{batchInfo ? ` (${batchInfo.current} of ${batchInfo.total})` : ""}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Preparing draft…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs">
                {batchInfo && <span className="rounded-full bg-violet-100 px-2 py-0.5 font-semibold text-violet-700" data-testid="batch-progress">Batch {batchInfo.current}/{batchInfo.total}</span>}
                <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-700" data-testid="prepare-step">{STEP_LABEL[step] || "Email"}</span>
                {fromCache
                  ? <span className="font-medium text-violet-600" data-testid="prepare-from-cache">AI draft loaded — review &amp; send.</span>
                  : <span className="text-slate-500">Cold lead — send from your own mailbox.</span>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={getBrief} disabled={briefLoading} data-testid="ai-brief-btn">
                  {briefLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Lightbulb className="mr-1 h-3.5 w-3.5 text-amber-500" />}
                  AI brief
                </Button>
                <Button variant="outline" size="sm" onClick={aiDraft} disabled={aiLoading} data-testid="ai-draft-btn">
                  {aiLoading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5 text-indigo-500" />}
                  AI draft
                </Button>
              </div>
            </div>
            {brief && (
              <div className="whitespace-pre-wrap rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-slate-700" data-testid="ai-brief-box">{brief}</div>
            )}
            <div className="grid gap-1.5"><Label>To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="prepare-to" /></div>
            <div className="grid gap-1.5"><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="prepare-subject" /></div>
            <div className="grid gap-1.5"><Label>Body</Label><Textarea rows={12} value={body} onChange={(e) => setBody(e.target.value)} data-testid="prepare-body" className="text-xs leading-relaxed" /></div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={copyBody} disabled={loading} data-testid="prepare-copy">
            {copied ? <Check className="mr-1 h-4 w-4 text-emerald-600" /> : <Copy className="mr-1 h-4 w-4" />}Copy
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" asChild disabled={loading}>
              <a href={mailto} data-testid="prepare-open"><Mail className="mr-1 h-4 w-4" />Open in email client</a>
            </Button>
            <Button onClick={markSent} disabled={loading} data-testid="prepare-mark-sent" style={{ backgroundColor: "hsl(var(--brand))" }} className="text-white">{batchInfo ? "Mark sent & next" : "Mark as sent"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
